import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { UnifiedAuditRow } from '@/api/console';
import { Button, EmptyState, ErrorState, SkeletonTable } from '@/components/ui';
import { AuditFilterBar, type AuditDateRangeValue } from './Filters';
import type { AuditFilters } from './auditFilterTypes';
import type { UseAuditFiltersResult } from './useAuditFilters';
import { useAuditFacets } from './useAuditFacets';
import { AuditFilterChips } from './AuditFilterChips';
import { AuditTable } from './AuditTable';
import { AuditPagination } from './AuditPagination';
import styles from './AuditPanelShell.module.css';

export type AuditSourceKind = 'account' | 'admin';

export interface AuditPanelShellProps {
    /** Which audit table is being shown — decides facets, columns and copy. */
    kind: AuditSourceKind;
    /** The whole `useAuditFilters` result (hook-model prop, see CLAUDE.md). */
    model: UseAuditFiltersResult;
    /** Server-owned numbers. `total`/`totalPages` are the API's, never derived from `rows`. */
    data: { rows: UnifiedAuditRow[]; total: number; totalPages: number; pageSize: number };
    /** The subset of the react-query result this shell renders. */
    query: {
        isLoading: boolean;
        isError: boolean;
        isSuccess: boolean;
        isFetching: boolean;
        refetch: () => void;
    };
    /**
     * Account source only: pins "Đối tượng" to one username (the profile tab)
     * so the operator cannot widen a per-account log into the global one.
     */
    lockedTarget?: string;
}

/**
 * EAA-1821: everything `AccountAuditPanel` and `AdminAuditPanel` have in
 * common — which is nearly all of it.
 *
 * The two panels used to be a copy-paste pair: the same filter bar, the same
 * SkeletonTable → ErrorState → EmptyState → table+pagination ladder, written
 * twice and already drifting (only one of them was translated, and their
 * `COLUMN_COUNT`s disagreed). They differ in exactly three things — the
 * endpoint, which of `credentialMode`/`targetType` they offer, and their
 * action vocabulary — so the endpoint stays in the panel and the other two are
 * derived here from `kind`.
 */
export function AuditPanelShell({
    kind,
    model,
    data,
    query,
    lockedTarget,
}: AuditPanelShellProps): React.ReactElement {
    const { t } = useTranslation();
    // EAA-1821: destructured rather than used as `model.x` inside the callbacks
    // below — `model` is a fresh object on every render, so a dep array holding
    // it would rebuild every handler each time and break the memo on the chips.
    const { filters, setFilters, page, setPage } = model;
    const { refetch } = query;
    const isAccount = kind === 'account';

    const { options, barLabels, chipLabels, searchPlaceholder, searchClearLabel } =
        useAuditFacets(isAccount);

    // EAA-1821: a locked target must survive every other filter change, so it
    // is re-applied on the way out instead of trusting each call site.
    const applyFilters = useCallback(
        (next: AuditFilters) => {
            setFilters(lockedTarget ? { ...next, target: lockedTarget } : next);
        },
        [setFilters, lockedTarget]
    );

    const handleSearchChange = useCallback(
        (search: string) => applyFilters({ ...filters, search }),
        [applyFilters, filters]
    );
    const handleActionChange = useCallback(
        (action: string[]) => applyFilters({ ...filters, action }),
        [applyFilters, filters]
    );
    const handleCredentialModeChange = useCallback(
        (credentialMode: string[]) => applyFilters({ ...filters, credentialMode }),
        [applyFilters, filters]
    );
    const handleTargetTypeChange = useCallback(
        (targetType: string[]) => applyFilters({ ...filters, targetType }),
        [applyFilters, filters]
    );
    const handleDateChange = useCallback(
        (date: AuditDateRangeValue) => applyFilters({ ...filters, date }),
        [applyFilters, filters]
    );

    // EAA-1821: "Kết quả" is one value on the wire (`success=true|false`) while
    // the chip is a checkbox list — ticking both boxes says the same thing as
    // ticking neither ("any result"), so both collapse to the empty filter
    // instead of sending a contradictory pair the backend would have to
    // interpret. The read side of that bridge lives in `AuditFilterChips`.
    const handleSuccessChange = useCallback(
        (next: string[]) => {
            applyFilters({ ...filters, success: next.length === 1 ? next[0] : '' });
        },
        [applyFilters, filters]
    );

    const searchProp = useMemo(
        () => ({
            value: filters.search,
            onChange: handleSearchChange,
            placeholder: searchPlaceholder,
            clearLabel: searchClearLabel,
        }),
        [filters.search, handleSearchChange, searchPlaceholder, searchClearLabel]
    );

    const chipHandlers = useMemo(
        () => ({
            onActionChange: handleActionChange,
            onSuccessChange: handleSuccessChange,
            onCredentialModeChange: handleCredentialModeChange,
            onTargetTypeChange: handleTargetTypeChange,
            onDateChange: handleDateChange,
        }),
        [
            handleActionChange,
            handleSuccessChange,
            handleCredentialModeChange,
            handleTargetTypeChange,
            handleDateChange,
        ]
    );

    const chips = (
        <AuditFilterChips
            isAccount={isAccount}
            filters={filters}
            options={options}
            handlers={chipHandlers}
            labels={chipLabels}
        />
    );

    const handlePrev = useCallback(() => setPage(page - 1), [setPage, page]);
    const handleNext = useCallback(() => setPage(page + 1), [setPage, page]);
    const handleRetry = useCallback(() => refetch(), [refetch]);

    const rows = data.rows;
    // Account rows carry the extra "Chế độ" column, so its skeleton is a column wider.
    const columnCount = isAccount ? 7 : 6;
    const hasRows = query.isSuccess && rows.length > 0;

    return (
        <div className={styles.panel}>
            {/* EAA-1821: the bar sits OUTSIDE every state branch on purpose. It
                used to render only alongside the table, so an over-narrowed
                filter emptied the screen and took its own controls with it —
                the operator was left with a lone button inside the empty state
                and no way to see, let alone relax, what was filtering. */}
            <AuditFilterBar
                className={styles.filterBar}
                search={searchProp}
                chips={chips}
                activeCount={model.activeCount}
                onClear={model.clear}
                shown={rows.length}
                total={data.total}
                labels={barLabels}
            />

            <div className={`${styles.panelBody} fit-grow`}>
                {query.isLoading && (
                    <div role="status" aria-live="polite">
                        <span className="portal-sr-only">
                            {t('common.loading', { defaultValue: 'Đang tải...' })}
                        </span>
                        <SkeletonTable rows={5} columns={columnCount} />
                    </div>
                )}

                {query.isError && (
                    <ErrorState
                        description={
                            isAccount
                                ? t('console.audit.loadErrorAccount', {
                                      defaultValue: 'Không thể tải nhật ký định danh.',
                                  })
                                : t('console.audit.loadErrorAdmin', {
                                      defaultValue: 'Không thể tải nhật ký quản trị.',
                                  })
                        }
                        onRetry={handleRetry}
                        retrying={query.isFetching}
                    />
                )}

                {query.isSuccess && rows.length === 0 && (
                    <EmptyState
                        title={t('console.audit.emptyTitle', {
                            defaultValue: 'Không có bản ghi phù hợp',
                        })}
                        action={
                            model.activeCount > 0 ? (
                                <Button onClick={model.clear}>{barLabels.clear}</Button>
                            ) : undefined
                        }
                    />
                )}

                {hasRows && (
                    <AuditTable
                        rows={rows}
                        showCredentialMode={isAccount}
                        sort={model.sort}
                        onSortChange={model.setSortField}
                        filters={filters}
                        onFiltersChange={applyFilters}
                        options={options}
                    />
                )}
            </div>

            {hasRows && (
                <AuditPagination
                    rowCount={rows.length}
                    total={data.total}
                    pageSize={data.pageSize}
                    page={model.page}
                    totalPages={data.totalPages}
                    onPrev={handlePrev}
                    onNext={handleNext}
                />
            )}
        </div>
    );
}
